import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  BookOpen,
  Calendar as CalendarIcon,
  Users as RosterIcon,
  MessageCircle,
  GraduationCap,
  BarChart3,
  Plus,
  ChevronRight,
  Loader2,
  Sparkles,
  AlertCircle,
  Megaphone,
} from 'lucide-react';
import type { ModuleItemType, ModuleItemShape, ModuleShape } from '@eduborg/shared';
import { AppHeader } from '@/components/AppHeader';
import { BlockEditor } from '@/components/BlockEditor/BlockEditor';
import { DiscussionsTab } from '@/components/courses/DiscussionsTab';
import { AnalyticsTab } from '@/components/courses/AnalyticsTab';
import {
  AnnouncementComposer,
  CourseAnnouncementsStrip,
} from '@/components/courses/AnnouncementComposer';
import { Field } from '@/components/ui/Field';
import { useAuth } from '@/lib/auth-store';
import { meetsMinRole } from '@/lib/roles';
import {
  useCourseDetailQuery,
  useCreateItemMutation,
  useCreateModuleMutation,
  useItemsQuery,
  useModulesQuery,
} from '@/hooks/queries/useCourseDetail';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type Tab = 'content' | 'calendar' | 'roster' | 'discussions' | 'gradebook' | 'analytics';

/**
 * The course-detail surface. Left-aligned tabs are operational (Content,
 * Calendar, Roster, Discussions, Gradebook, Analytics). Right-aligned panels
 * — the EduBorg agent, Spark gallery, Tutoring Center, Announcements,
 * Settings — slot in next to these as Phase 5+ ships.
 *
 * Phase 2 ships Content fully + the rest as placeholders so the navigation
 * shape is real and the empty states tell the user what's coming.
 */
export function CourseDetail() {
  const { state } = useAuth();
  const { orgSlug = '', courseSlug = '' } = useParams();
  const [tab, setTab] = useState<Tab>('content');

  if (state.status !== 'authenticated') return null;
  const token = state.token;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />
      <CourseDetailInner
        token={token}
        orgSlug={orgSlug}
        courseSlug={courseSlug}
        tab={tab}
        onTabChange={setTab}
      />
    </div>
  );
}

function CourseDetailInner({
  token,
  orgSlug,
  courseSlug,
  tab,
  onTabChange,
}: {
  token: string;
  orgSlug: string;
  courseSlug: string;
  tab: Tab;
  onTabChange: (t: Tab) => void;
}) {
  const courseQuery = useCourseDetailQuery(token, orgSlug, courseSlug);
  const { state: authState } = useAuth();

  function computeCanEdit(course: { creatorProfileId: string; orgId: string }): boolean {
    if (authState.status !== 'authenticated') return false;
    const { profile } = authState;
    if (course.creatorProfileId === profile.id) return true;
    return meetsMinRole(profile.roles, course.orgId, 'instructor');
  }

  if (courseQuery.isLoading) {
    return (
      <main className="container py-10" aria-live="polite">
        <div className="h-8 w-2/5 animate-pulse rounded-md bg-card" />
        <div className="mt-3 h-4 w-3/5 animate-pulse rounded-md bg-card" />
        <div className="mt-8 h-64 animate-pulse rounded-2xl bg-card" />
      </main>
    );
  }

  if (courseQuery.isError || !courseQuery.data) {
    return (
      <main className="container py-10">
        <div className="rounded-2xl border border-border bg-card p-8 text-center">
          <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" aria-hidden />
          <h1 className="mt-3 font-display text-xl font-semibold">Course not found.</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            It may have been moved or you might not have access.
          </p>
          <Link
            to="/dashboard"
            className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Back to dashboard
          </Link>
        </div>
      </main>
    );
  }

  const course = courseQuery.data.course;
  const canEdit = computeCanEdit(course);

  return (
    <main className="container py-8">
      <CourseHeader course={course} orgSlug={orgSlug} canEdit={canEdit} token={token} />

      {/* Tab nav. We hide tabs whose surface isn't built yet, plus role-gate
          the ones that are inherently instructor-only:
            - Calendar / Roster / Gradebook are placeholders; show them only
              to instructors (so they can see what's coming) and hide from
              students entirely (no value, just dead clicks).
            - Analytics is always instructor-only.
            - Content + Discussions are real for everyone. */}
      <nav className="mb-6 flex flex-wrap items-center gap-1 border-b border-border">
        <TabBtn current={tab} value="content" onClick={onTabChange} icon={BookOpen}>
          Content
        </TabBtn>
        {canEdit && (
          <TabBtn current={tab} value="calendar" onClick={onTabChange} icon={CalendarIcon}>
            Calendar
          </TabBtn>
        )}
        {canEdit && (
          <TabBtn current={tab} value="roster" onClick={onTabChange} icon={RosterIcon}>
            Roster
          </TabBtn>
        )}
        <TabBtn current={tab} value="discussions" onClick={onTabChange} icon={MessageCircle}>
          Discussions
        </TabBtn>
        {canEdit && (
          <TabBtn current={tab} value="gradebook" onClick={onTabChange} icon={GraduationCap}>
            Gradebook
          </TabBtn>
        )}
        {canEdit && (
          <TabBtn current={tab} value="analytics" onClick={onTabChange} icon={BarChart3}>
            Analytics
          </TabBtn>
        )}
      </nav>

      {/* AnimatePresence + motion.section was deadlocking on tab switches —
          the new section stayed at initial opacity:0 because the prior
          section's exit never completed. The fade was nice but not worth
          the broken nav. Plain conditional rendering until we understand
          the framer-motion interaction better. */}
      <section>
        {tab === 'content' && (
          <ContentTab
            token={token}
            courseId={course.id}
            orgSlug={orgSlug}
            courseSlug={courseSlug}
            canEdit={canEdit}
          />
        )}
        {tab === 'calendar' && (
          <PhasePlaceholder
            title="Calendar"
            body="Course calendar — schedule, replacement tags, AI-generated weekly reminders. Coming soon."
          />
        )}
        {tab === 'roster' && (
          <PhasePlaceholder
            title="Roster"
            body="Student / TA list, invites, bulk-add by CSV, and per-student engagement signal. Coming soon."
          />
        )}
        {tab === 'discussions' && <DiscussionsTab token={token} courseId={course.id} />}
        {tab === 'gradebook' && (
          <PhasePlaceholder
            title="Gradebook"
            body="Weighted + points modes, color-coded categories, drill-downs. Deterministic grading; AI suggests feedback. Coming soon."
          />
        )}
        {tab === 'analytics' && (
          <AnalyticsTab token={token} courseId={course.id} canEdit={canEdit} />
        )}
      </section>
    </main>
  );
}

/* ─── Content tab — modules + items + lesson editor ──────────────────── */

function ContentTab({
  token,
  courseId,
  orgSlug,
  courseSlug,
  canEdit,
}: {
  token: string;
  courseId: string;
  orgSlug: string;
  courseSlug: string;
  canEdit: boolean;
}) {
  const modulesQuery = useModulesQuery(token, courseId);
  const createModule = useCreateModuleMutation(token, courseId);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  if (modulesQuery.isLoading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-card" aria-live="polite" />;
  }

  const modules = modulesQuery.data?.modules ?? [];
  // Top-level modules first; nested levels render under their parent.
  const topLevel = modules.filter((m) => m.parentModuleId === null);

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_1fr]">
      {/* Module + item tree */}
      <aside className="space-y-3">
        <div className="rounded-2xl border border-border bg-card">
          <header className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="font-display text-sm font-semibold">Modules</h2>
            {canEdit && (
              <button
                type="button"
                onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden /> Module
              </button>
            )}
          </header>

          {topLevel.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No modules yet. Add your first one to get started.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {topLevel.map((m) => (
                <ModuleNode
                  key={m.id}
                  token={token}
                  module={m}
                  allModules={modules}
                  selectedItemId={selectedItemId}
                  onSelect={setSelectedItemId}
                  orgSlug={orgSlug}
                  courseSlug={courseSlug}
                  canEdit={canEdit}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* Lesson editor (or empty state). Empty-state language adapts to
          the viewer — instructors get build-context, students get a
          read-context invitation. */}
      <section className="min-h-[24rem]">
        {selectedItemId ? (
          <BlockEditor token={token} itemId={selectedItemId} readOnly={!canEdit} />
        ) : (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-10 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-accent" aria-hidden />
            <h3 className="mt-3 font-display text-lg font-semibold">
              {canEdit ? 'Select a lesson to edit it.' : 'Pick a lesson to start.'}
            </h3>
            <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
              {canEdit
                ? 'Or add a module on the left and drop a lesson into it. EduBorg will help draft.'
                : 'Click any lesson on the left to read it. Your teacher decides what to add — discussions and grades live in the tabs above.'}
            </p>
          </div>
        )}
      </section>

      {adding && (
        <NewModuleDialog
          onClose={() => setAdding(false)}
          onSubmit={async (title) => {
            await createModule.mutateAsync({ title });
            setAdding(false);
          }}
          pending={createModule.isPending}
        />
      )}
    </div>
  );
}

function ModuleNode({
  token,
  module: m,
  allModules,
  selectedItemId,
  onSelect,
  orgSlug,
  courseSlug,
  canEdit,
}: {
  token: string;
  module: ModuleShape;
  allModules: ModuleShape[];
  selectedItemId: string | null;
  onSelect: (id: string) => void;
  orgSlug: string;
  courseSlug: string;
  canEdit: boolean;
}) {
  const itemsQuery = useItemsQuery(token, m.id);
  const createItem = useCreateItemMutation(token, m.id);
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const items = itemsQuery.data?.items ?? [];
  const children = allModules.filter((c) => c.parentModuleId === m.id);

  return (
    <li className="px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-sm font-semibold">{m.title}</span>
        {canEdit && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <Plus className="h-3 w-3" aria-hidden /> Item
          </button>
        )}
      </div>

      {items.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {items.map((it) => (
            <li key={it.id}>
              <ItemRow
                item={it}
                selected={selectedItemId === it.id}
                onSelectLesson={() => onSelect(it.id)}
                orgSlug={orgSlug}
                courseSlug={courseSlug}
                canEdit={canEdit}
              />
            </li>
          ))}
        </ul>
      )}

      {children.length > 0 && (
        <ul className="ml-3 mt-2 space-y-2 border-l border-border pl-3">
          {children.map((child) => (
            <ModuleNode
              key={child.id}
              token={token}
              module={child}
              allModules={allModules}
              selectedItemId={selectedItemId}
              onSelect={onSelect}
              orgSlug={orgSlug}
              courseSlug={courseSlug}
              canEdit={canEdit}
            />
          ))}
        </ul>
      )}

      {adding && (
        <NewItemDialog
          onClose={() => setAdding(false)}
          onSubmit={async ({ type, title }) => {
            // Assessment-type items need both a module_item AND an assessment
            // row, which the /assessments POST endpoint creates atomically.
            // Other item types just need the module_item.
            if (type === 'assessment' || type === 'knowledge_check') {
              const { moduleItemId } = await api.assessments.create(token, {
                courseId: m.courseId,
                moduleId: m.id,
                title,
                category: type,
              });
              await queryClient.invalidateQueries({ queryKey: ['module', m.id, 'items'] });
              onSelect(moduleItemId);
            } else {
              const created = await createItem.mutateAsync({ type, title });
              onSelect(created.item.id);
            }
            setAdding(false);
          }}
          pending={createItem.isPending}
        />
      )}
    </li>
  );
}

function ItemRow({
  item,
  selected,
  onSelectLesson,
  orgSlug,
  courseSlug,
  canEdit,
}: {
  item: ModuleItemShape;
  selected: boolean;
  onSelectLesson: () => void;
  orgSlug: string;
  courseSlug: string;
  canEdit: boolean;
}) {
  const isAssessment = item.type === 'assessment' || item.type === 'knowledge_check';

  if (isAssessment) {
    const target = canEdit
      ? `/c/${orgSlug}/${courseSlug}/assessments/${item.id}/edit`
      : `/c/${orgSlug}/${courseSlug}/assessments/${item.id}/take`;
    return (
      <Link
        to={target}
        className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <ItemTypeBadge type={item.type} />
        <span className="flex-1 truncate">{item.title}</span>
        <ChevronRight
          className="h-3.5 w-3.5 opacity-40 transition-opacity group-hover:opacity-100"
          aria-hidden
        />
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onSelectLesson}
      className={cn(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
        selected
          ? 'bg-accent/10 text-accent'
          : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
      )}
    >
      <ItemTypeBadge type={item.type} />
      <span className="flex-1 truncate">{item.title}</span>
    </button>
  );
}

function ItemTypeBadge({ type }: { type: ModuleItemShape['type'] }) {
  const map: Record<ModuleItemShape['type'], string> = {
    lesson: 'L',
    assignment: 'A',
    assessment: 'Q',
    knowledge_check: 'K',
    discussion: 'D',
    file: 'F',
    link: '↗',
    internal_link: '⇆',
  };
  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-secondary text-[10px] font-bold text-foreground">
      {map[type]}
    </span>
  );
}

function CourseHeader({
  course,
  orgSlug,
  canEdit,
  token,
}: {
  course: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    joinCode: string;
    publishedAsTemplate?: boolean | undefined;
  };
  orgSlug: string;
  canEdit: boolean;
  token: string;
}) {
  const [composing, setComposing] = useState(false);
  const queryClient = useQueryClient();
  const [togglingPublish, setTogglingPublish] = useState(false);

  async function togglePublishedAsTemplate() {
    if (togglingPublish) return;
    setTogglingPublish(true);
    try {
      await api.courses.update(token, course.id, {
        publishedAsTemplate: !course.publishedAsTemplate,
      });
      // Invalidate so the header re-renders with the new toggle state.
      void queryClient.invalidateQueries({ queryKey: ['course-detail'] });
    } catch (err) {
      console.error('[course] publishedAsTemplate toggle failed', err);
    } finally {
      setTogglingPublish(false);
    }
  }
  return (
    <>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {/* Instructors come from the Workspace; students come from their
                Dashboard "your classes". Different breadcrumb so it makes
                sense in both contexts. */}
            {canEdit ? (
              <Link to="/workspace" className="hover:text-foreground">
                Workspace
              </Link>
            ) : (
              <Link to="/dashboard" className="hover:text-foreground">
                My classes
              </Link>
            )}
            <span className="mx-1">/</span>
            <span>{orgSlug}</span>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">{course.title}</h1>
          {course.description && (
            <p className="max-w-2xl text-sm text-muted-foreground">{course.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          {/* Study Room — course-grounded tutor + time tracking. Visible to
              everyone; students are the primary audience but instructors
              can dogfood it on their own course. */}
          <StudyRoomLink />
          {canEdit && (
            <button
              type="button"
              onClick={() => setComposing(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-semibold text-accent transition-colors hover:bg-accent/5"
            >
              <Megaphone className="h-3.5 w-3.5" /> Announce
            </button>
          )}
          {/* Publish-as-template toggle. Instructor-only. Surfaces the
              course on the public Template Library at /templates so
              other instructors can clone it. Disabled if the course is
              still a draft — only active courses make sense to publish. */}
          {canEdit && (
            <button
              type="button"
              onClick={togglePublishedAsTemplate}
              disabled={togglingPublish || course.status !== 'active'}
              title={
                course.status !== 'active'
                  ? 'Only active courses can be published as templates.'
                  : course.publishedAsTemplate
                    ? 'Listed in the Template Library. Click to unlist.'
                    : 'Click to publish to the Template Library.'
              }
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50',
                course.publishedAsTemplate
                  ? 'border-accent/40 bg-accent/10 text-accent hover:bg-accent/15'
                  : 'border-border bg-card text-muted-foreground hover:bg-secondary',
              )}
            >
              {course.publishedAsTemplate ? 'Listed as template' : 'Publish as template'}
            </button>
          )}
          {/* Status pill + join-code are instructor-only — students don't
              need to know if a course is in DRAFT, and the code is a sharing
              affordance for the instructor (they share it with students,
              who don't need to see it once enrolled). */}
          {canEdit && (
            <>
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-medium uppercase tracking-wider',
                  course.status === 'active'
                    ? 'bg-accent/10 text-accent'
                    : course.status === 'archived'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-secondary text-foreground',
                )}
              >
                {course.status}
              </span>
              <span className="rounded-full border border-border bg-card px-2.5 py-0.5 text-xs text-muted-foreground">
                Code <span className="font-mono">{course.joinCode}</span>
              </span>
            </>
          )}
        </div>
      </header>

      <div className="mb-6">
        <CourseAnnouncementsStrip token={token} courseId={course.id} />
      </div>

      {composing && (
        <AnnouncementComposer
          token={token}
          courseId={course.id}
          onClose={() => setComposing(false)}
        />
      )}
    </>
  );
}

/**
 * "Study Room" CTA in the course header. Self-contained — pulls slugs
 * from useParams so the parent CourseHeader doesn't need to thread them
 * through. Visible to everyone on the course page.
 */
function StudyRoomLink() {
  const { orgSlug = '', courseSlug = '' } = useParams();
  return (
    <Link
      to={`/c/${orgSlug}/${courseSlug}/study`}
      className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-accent-foreground shadow-sm transition-shadow hover:shadow-md"
      title="Open the Study Room — course-grounded tutor with time tracking"
    >
      <GraduationCap className="h-3.5 w-3.5" /> Study Room
    </Link>
  );
}

function TabBtn({
  current,
  value,
  onClick,
  icon: Icon,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: (t: Tab) => void;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-accent text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className="h-4 w-4" aria-hidden />
      {children}
    </button>
  );
}

function PhasePlaceholder({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center">
      <Sparkles className="mx-auto h-7 w-7 text-accent" aria-hidden />
      <h3 className="mt-3 font-display text-xl font-semibold">{title}</h3>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

/* ─── Dialogs ─────────────────────────────────────────────────────────── */

function NewModuleDialog({
  onClose,
  onSubmit,
  pending,
}: {
  onClose: () => void;
  onSubmit: (title: string) => Promise<void>;
  pending: boolean;
}) {
  const [title, setTitle] = useState('');
  return (
    <DialogShell title="Add module" onClose={onClose}>
      <form
        className="space-y-3 px-6 py-5"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!title.trim()) return;
          await onSubmit(title.trim());
        }}
      >
        <Field
          label="Module title"
          autoFocus
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          hint="e.g. Week 1 — Cells, Unit 3 — Linear Functions"
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending || title.trim().length === 0}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-md disabled:cursor-not-allowed disabled:opacity-70"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Add
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function NewItemDialog({
  onClose,
  onSubmit,
  pending,
}: {
  onClose: () => void;
  onSubmit: (input: { type: ModuleItemType; title: string }) => Promise<void>;
  pending: boolean;
}) {
  const [type, setType] = useState<ModuleItemType>('lesson');
  const [title, setTitle] = useState('');
  const types: Array<{ value: ModuleItemType; label: string }> = [
    { value: 'lesson', label: 'Lesson' },
    { value: 'assignment', label: 'Assignment' },
    { value: 'assessment', label: 'Quiz' },
    { value: 'knowledge_check', label: 'Knowledge check' },
    { value: 'discussion', label: 'Discussion' },
    { value: 'link', label: 'Link' },
  ];
  return (
    <DialogShell title="Add item" onClose={onClose}>
      <form
        className="space-y-4 px-6 py-5"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!title.trim()) return;
          await onSubmit({ type, title: title.trim() });
        }}
      >
        <div>
          <span className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Type
          </span>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {types.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                className={cn(
                  'rounded-lg border px-2.5 py-1 text-xs font-medium',
                  type === t.value
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border bg-card text-muted-foreground hover:bg-secondary',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <Field
          label="Title"
          autoFocus
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending || title.trim().length === 0}
            className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-md disabled:cursor-not-allowed disabled:opacity-70"
          >
            {pending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            Add
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 backdrop-blur-sm sm:items-center">
      <div
        role="dialog"
        aria-label={title}
        className="w-full max-w-md overflow-hidden rounded-t-3xl border-x border-t border-border bg-background shadow-2xl sm:rounded-3xl sm:border"
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-3.5">
          <h3 className="font-display text-base font-semibold">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
          >
            <span className="text-lg leading-none">&times;</span>
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
